import { ThemeProvider } from "@mui/material/styles";
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DarkThemeWithResponsiveFontSizes as DarkTheme,
  LightThemeWithResponsiveFontSizes as LightTheme,
} from "../theme/theme";

import {
  DevDarkThemeWithResponsiveFontSizes,
  DevLightThemeWithResponsiveFontSizes,
} from "../theme/theme.dev";
import { getRuntimeEnv } from "./getRuntimeEnv";

type ColorModeContextType = {
  mode: string;
  toggleMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType,
);

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)",
  ).matches;

  const systemPreference = isSystemPreferenceDark ? "dark" : "light";
  const [mode, setMode] = useState(
    colorModeFromLocalStorage ?? systemPreference,
  );
  const runtimeEnv = useMemo(() => getRuntimeEnv(), []);
  const isDevEnv = runtimeEnv === "dev";

  const theme = useMemo(() => {
    if (isDevEnv) {
      return mode === "light"
        ? DevLightThemeWithResponsiveFontSizes
        : DevDarkThemeWithResponsiveFontSizes;
    }
    return mode === "light" ? LightTheme : DarkTheme;
  }, [mode, isDevEnv]);

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
  }, [mode]);

  const toggleColorMode = useCallback(() => {
    if (mode === "light") {
      setMode("dark");
    } else {
      setMode("light");
    }
  }, [mode]);

  const colorModeValue = useMemo(
    () => ({
      toggleMode: toggleColorMode,
      mode,
    }),
    [mode, toggleColorMode],
  );

  return (
    <ColorModeContext.Provider value={colorModeValue}>
      <ThemeProvider
        // you can change the theme colors here. example: mode === "light" ? RefineThemes.Magenta : RefineThemes.MagentaDark
        theme={theme}
      >
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
